import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import { videos, categories } from "@/mocks/videos";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const videoId = Number(id);
  const video = videos.find((v) => v.id === videoId);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  const category = categories.find((c) => c.id === video?.category);
  const relatedVideos = video
    ? videos
        .filter((v) => v.id !== video.id && v.category === video.category)
        .slice(0, 4)
    : [];

  const articleSchema = video
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: video.title,
        description: video.description,
        image: video.coverImage,
        datePublished: video.updatedAt,
        author: {
          "@type": "Organization",
          name: video.source,
        },
        publisher: {
          "@type": "Organization",
          name: "玫瑰视频",
          url: typeof window !== "undefined" ? window.location.origin : "",
        },
      }
    : null;

  const breadcrumbSchema = video
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "首页",
            item: typeof window !== "undefined" ? window.location.origin : "",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: category?.name || "分类",
            item: `${typeof window !== "undefined" ? window.location.origin : ""}/category/${video.category}`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: video.title,
          },
        ],
      }
    : null;

  useEffect(() => {
    if (!articleSchema || !breadcrumbSchema) return;

    const script1 = document.createElement("script");
    script1.type = "application/ld+json";
    script1.textContent = JSON.stringify(articleSchema);
    document.head.appendChild(script1);

    const script2 = document.createElement("script");
    script2.type = "application/ld+json";
    script2.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(script2);

    return () => {
      document.head.removeChild(script1);
      document.head.removeChild(script2);
    };
  }, [id]);

  if (!video) {
    return (
      <div className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
            <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-semibold text-foreground-800 mb-2">影片未找到</h1>
          <p className="text-foreground-500 text-sm mb-6">该影片可能已下架或不存在</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i> 返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16">
      <div className="max-w-5xl mx-auto px-4 md:px-6 pt-6">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: category?.name || "分类", to: `/category/${video.category}` },
            { label: video.title },
          ]}
        />

        <article className="py-8">
          <div className="relative rounded-2xl overflow-hidden mb-8">
            <img
              src={video.coverImage}
              alt={`${video.title} - 玫瑰视频高清在线观看`}
              title={`${video.title} - 玫瑰视频`}
              className="w-full aspect-video md:aspect-[21/9] object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <div className="flex items-center gap-3 mb-3">
                <span className="px-2 py-0.5 bg-primary-500 text-background-50 text-xs font-medium rounded-md">
                  {video.rating} 分
                </span>
                <span className="text-sm text-background-50/80">{video.source}</span>
                <span className="text-sm text-background-50/80">{video.duration}</span>
              </div>
            </div>
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3 font-heading">
            {video.title}
          </h1>

          <div className="flex flex-wrap items-center gap-3 mb-6">
            {video.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full"
              >
                {tag}
              </span>
            ))}
            <span className="text-xs text-foreground-400">
              {video.views} 次播放 · 更新于 {video.updatedAt}
            </span>
          </div>

          <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed space-y-4 mb-10">
            <div dangerouslySetInnerHTML={{ __html: video.content }} />
          </div>

          {video.totalEpisodes > 1 && (
            <div className="bg-background-100 rounded-2xl p-6 mb-10">
              <h3 className="text-base font-semibold text-foreground-800 mb-4">
                剧集列表（共{video.totalEpisodes}集）
              </h3>
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                {Array.from({ length: video.totalEpisodes }, (_, i) => (
                  <button
                    key={i}
                    className="px-3 py-2 rounded-lg bg-background-50 text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer text-center whitespace-nowrap"
                  >
                    第{i + 1}集
                  </button>
                ))}
              </div>
            </div>
          )}
        </article>

        {relatedVideos.length > 0 && (
          <section className="py-10 border-t border-background-200">
            <h2 className="text-xl font-bold text-foreground-950 mb-6">相关推荐</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {relatedVideos.map((v) => (
                <Link
                  key={v.id}
                  to={`/article/${v.id}`}
                  className="group cursor-pointer"
                  data-product-shop
                >
                  <div className="relative overflow-hidden rounded-xl mb-3">
                    <img
                      src={v.coverImage}
                      alt={`${v.title} - 玫瑰视频`}
                      className="w-full aspect-video object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2 py-0.5 bg-primary-500 text-background-50 text-xs font-medium rounded-md">
                        {v.rating}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                    {v.title}
                  </h3>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}